/**
 * AirdropHistory Component
 *
 * Displays the history of all airdrop transfers with filtering,
 * search, and pagination capabilities.
 *
 * @component
 */

import React, { useState, useEffect, useMemo } from "react";
import { getRequest } from "../../utils/httpRequest";

// Shared imports
import { ITEMS_PER_PAGE, API_ENDPOINTS } from "./constants";
import {
  formatDate,
  formatAmount,
  getHashscanUrl,
  calculateHistoryStats,
} from "./utils";
import { commonStyles } from "./styles";
import {
  Pagination,
  StatsCard,
  StatsContainer,
  LoadingSpinner,
  SectionHeader,
  FilterBar,
  SearchInput,
  StatusSelect,
  ResultsBadge,
  GhostButton,
} from "./components";

// ============================================================================
// Sub-components
// ============================================================================

/**
 * HistoryStats - Statistics display for airdrop history
 */
const HistoryStats = ({ history }) => {
  const stats = useMemo(() => calculateHistoryStats(history), [history]);

  return (
    <StatsContainer>
      <StatsCard
        value={stats.total}
        label="Total Transfers"
        variant="primary"
      />
      <StatsCard
        value={stats.successful}
        label="Successful"
        variant="success"
      />
      <StatsCard value={stats.failed} label="Failed" variant="danger" />
      <StatsCard
        value={formatAmount(stats.totalAmount)}
        label="Total Amount"
        variant="accent"
      />
    </StatsContainer>
  );
};

/**
 * HistoryTable - Table displaying transfer records
 */
const HistoryTable = ({ transfers, hederaNetwork }) => (
  <div
    style={{
      ...commonStyles.panel,
      marginBottom: 0,
    }}
  >
    <h3 style={commonStyles.panelHeader}>Transfer Records</h3>
    <div className="index_table_container">
      <table className="index_table history-table">
        <thead>
          <tr>
            <th className="col col-id">ID</th>
            <th className="col col-recipient">Recipient</th>
            <th className="col col-token">Token ID</th>
            <th className="col col-amount">Amount</th>
            <th className="col col-transaction">Transaction</th>
            <th className="col col-status">Status</th>
            <th className="col col-date">Date</th>
          </tr>
        </thead>
        <tbody>
          {transfers.map((item, idx) => (
            <tr
              key={item.id}
              className={`${item.status !== "success" ? "error-row" : ""} ${
                idx % 2 === 0 ? "even" : "odd"
              }`}
            >
              <td className="col col-id">
                {typeof item.id === "string" && item.id.length > 8
                  ? `${item.id.substring(0, 8)}...`
                  : item.id}
              </td>
              <td className="col col-recipient">
                <code>{item.recipientAccountId}</code>
              </td>
              <td className="col col-token">
                <code>{item.tokenId}</code>
              </td>
              <td className="col col-amount">{formatAmount(item.amount)}</td>
              <td className="col col-transaction">
                {item.transactionId ? (
                  <a
                    href={getHashscanUrl(item.transactionId, hederaNetwork)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={item.transactionId}
                    className="tx-link"
                  >
                    {item.transactionId.length > 20
                      ? `${item.transactionId.substring(0, 20)}...`
                      : item.transactionId}
                  </a>
                ) : (
                  "-"
                )}
              </td>
              <td className="col col-status">
                <span className={`status_tag ${item.status}`}>
                  {item.status === "success" ? "Success" : "Failed"}
                </span>
              </td>
              <td className="col col-date">{formatDate(item.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

const AirdropHistory = ({ hederaNetwork = "testnet" }) => {
  // State
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiMessage, setApiMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Fetch history on mount
  useEffect(() => {
    fetchHistory();
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  // Data fetching
  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    setApiMessage(null);
    try {
      const data = await getRequest(API_ENDPOINTS.AIRDROP_HISTORY);
      if (data.success) {
        const transfers = data.data?.transfers || [];
        setHistory(transfers);
        if (data.data?.message) {
          setApiMessage(data.data.message);
        }
        if (data.data?.error) {
          setApiMessage(`API Error: ${data.data.error}`);
        }
      } else {
        setError(data.error || "Failed to load history");
      }
    } catch (err) {
      console.error("Error fetching history:", err);
      setHistory([]);
      setApiMessage("Unable to fetch history from the liquidity pool API");
    } finally {
      setLoading(false);
    }
  };

  // Filtered and paginated data (memoized for performance)
  const filteredHistory = useMemo(() => {
    let filtered = history;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.recipientAccountId?.toLowerCase().includes(query) ||
          item.tokenId?.toLowerCase().includes(query) ||
          item.transactionId?.toLowerCase().includes(query),
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((item) => item.status === statusFilter);
    }

    return filtered;
  }, [history, searchQuery, statusFilter]);

  const totalPages = Math.ceil(filteredHistory.length / ITEMS_PER_PAGE);

  const paginatedHistory = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredHistory.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredHistory, currentPage]);

  // Status filter options
  const statusOptions = [
    { value: "all", label: "All" },
    { value: "success", label: "Success" },
    { value: "failed", label: "Failed" },
  ];

  // Loading state
  if (loading) {
    return (
      <div className="airdrop-history">
        <LoadingSpinner message="Loading airdrop history..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="airdrop-history">
        <div
          className="flash flash_error"
          style={{ textAlign: "center", padding: "24px" }}
        >
          <p style={{ margin: "0 0 12px" }}>{error}</p>
          <button className="btn btn-primary" onClick={fetchHistory}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="airdrop-history">
      {/* Header */}
      <SectionHeader title="Airdrop History">
        <GhostButton onClick={fetchHistory}>Refresh</GhostButton>
      </SectionHeader>

      {/* API Message */}
      {apiMessage && (
        <div className="flash flash_warning" style={{ marginBottom: "16px" }}>
          {apiMessage}
        </div>
      )}

      {/* Stats Summary */}
      <HistoryStats history={history} />

      {/* Filters */}
      <FilterBar>
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by recipient, token ID, or transaction..."
          style={{ maxWidth: "400px" }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <StatusSelect
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
          />
          <ResultsBadge
            showing={paginatedHistory.length}
            total={filteredHistory.length}
          />
        </div>
      </FilterBar>

      {/* Content */}
      {filteredHistory.length === 0 ? (
        <div className="blank_slate">
          <p>
            {searchQuery || statusFilter !== "all"
              ? "No transfers match your filters"
              : "No airdrop history found. Once you execute an airdrop, transfers will appear here."}
          </p>
        </div>
      ) : (
        <>
          <HistoryTable
            transfers={paginatedHistory}
            hederaNetwork={hederaNetwork}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredHistory.length}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </>
      )}
    </div>
  );
};

export default AirdropHistory;
